import { invalidatePageDataCacheEntries } from "../hooks/usePageData.js";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";

const ROOT = "/api/billing";
/** Must match APPROVED_BLOCKED_FILTER_CODE in billingPresentation.js */
const APPROVED_BLOCKED_FILTER_CODE = "__approved__";
const clean = (value) => String(value ?? "").trim();
const id = (value) => encodeURIComponent(clean(value));

export class BillingApiError extends Error {
  constructor(message, status = null, payload = null) {
    super(message);
    this.name = "BillingApiError";
    this.status = status;
    this.payload = payload;
  }
}

function issueMessage(issue) {
  if (typeof issue === "string") return issue.trim();
  if (!issue || typeof issue !== "object") return "";
  const message = clean(issue.message || issue.msg || issue.detail);
  if (!message) return "";
  const location = Array.isArray(issue.loc)
    ? issue.loc.filter((part) => part !== "body").map(clean).filter(Boolean).join(" › ")
    : "";
  return location ? `${location}: ${message}` : message;
}

export function billingErrorMessage(payload, fallback = "La requête de facturation a échoué.") {
  const detail = payload?.detail;
  const primary = clean(
    typeof detail === "string" ? detail
      : typeof detail?.detail === "string" ? detail.detail
        : payload?.message,
  ) || fallback;
  const rawIssues = Array.isArray(detail)
    ? detail
    : Array.isArray(detail?.issues) ? detail.issues
      : Array.isArray(payload?.issues) ? payload.issues : [];
  const issues = rawIssues.map(issueMessage).filter(Boolean).slice(0, 3);
  if (!issues.length) return primary;
  const suffix = issues.join(" · ");
  return primary.includes(suffix) ? primary : `${primary} — ${suffix}`;
}

// Coupure réseau : message français au lieu de « Failed to fetch » ; un abandon reste un abandon.
function billingFetch(url, init) {
  return fetchWithSupabaseAuth(url, init).catch((error) => {
    if (error?.name === "AbortError") throw error;
    throw new BillingApiError("Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.");
  });
}

function headers(companyId, json = false) {
  const value = new Headers();
  if (json) value.set("Content-Type", "application/json");
  if (clean(companyId)) value.set("X-Company-ID", clean(companyId));
  return value;
}

async function request(path, options = {}, fallback = "La requête de facturation a échoué.") {
  const { body, companyId, method = "GET", signal } = options;
  const response = await billingFetch(`${ROOT}${path}`, {
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: "no-store",
    headers: headers(companyId, body !== undefined),
    method,
    signal,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new BillingApiError(billingErrorMessage(payload, fallback), response.status, payload);
  return payload ?? {};
}

export function buildBillingQuery(filters = {}) {
  const query = new URLSearchParams();
  const blockingReason = clean(filters.blockingReason ?? filters.blocking_reason);
  const approvedBlocked = Boolean(
    filters.approvedBlocked
    ?? filters.approved_blocked
    ?? (blockingReason === APPROVED_BLOCKED_FILTER_CODE),
  );
  const fields = {
    client_id: filters.clientId ?? filters.client_id,
    client_name: filters.clientName ?? filters.client_name,
    billing_account_id: filters.billingAccountId ?? filters.billing_account_id,
    billing_side: filters.billingSide ?? filters.billing_side,
    cursor: filters.cursor,
    from_date: filters.fromDate ?? filters.from_date,
    limit: filters.limit,
    search: filters.search,
    status: filters.status,
    to_date: filters.toDate ?? filters.to_date,
    blocking_reason: approvedBlocked ? "" : blockingReason,
    issue_checks: filters.issueChecks ?? filters.issue_checks,
  };
  Object.entries(fields).forEach(([key, value]) => {
    if (clean(value)) query.set(key, clean(value));
  });
  if (approvedBlocked) query.set("approved_blocked", "true");
  if (filters.excludeAllocated === false || filters.exclude_allocated === false) {
    query.set("exclude_allocated", "false");
  }
  return query.size ? `?${query.toString()}` : "";
}

export function buildIssuerSettingsPayload(settings = {}, initial = null) {
  // initial : valeurs à l'ouverture de la fenêtre ; sans elles, tout est envoyé.
  const changed = (key) => !initial || clean(settings[key]) !== clean(initial[key]);
  return {
    legal_name: clean(settings.legal_name),
    address: clean(settings.address),
    phone: clean(settings.phone) || null,
    email: clean(settings.email) || null,
    gst_registration_number: clean(settings.gst_registration_number) || null,
    qst_registration_number: clean(settings.qst_registration_number) || null,
    gst_rate_percent: clean(settings.gst_rate_percent) || "5.000",
    qst_rate_percent: clean(settings.qst_rate_percent) || "9.975",
    taxes_enabled: Boolean(settings.taxes_enabled),
    ...(clean(settings.aspiration_fixed_rate) && changed("aspiration_fixed_rate") ? { aspiration_fixed_rate: clean(settings.aspiration_fixed_rate) } : {}),
    ...(settings.aspiration_fixed_rate_scope && changed("aspiration_fixed_rate_scope") ? { aspiration_fixed_rate_scope: settings.aspiration_fixed_rate_scope } : {}),
    ...(clean(settings.fuel_surcharge_percent) ? { fuel_surcharge_percent: clean(settings.fuel_surcharge_percent) } : {}),
    // Vide : aucun tarif dompeur ; la clé est envoyée pour pouvoir l'effacer.
    ...(changed("dompeur_tonne_rate") ? { dompeur_tonne_rate: clean(settings.dompeur_tonne_rate) || null } : {}),
    version: settings.version,
  };
}

export function buildBillingAccountPayload(account = {}) {
  const base = {
    name: clean(account.name),
    billing_address: clean(account.billing_address),
    billing_email: clean(account.billing_email),
    statement_link_days: Number(account.statement_link_days || 90),
    client_ids: Array.from(new Set((account.client_ids || []).map(clean).filter(Boolean))),
    version: account.version,
    ...Object.fromEntries(["normal_hourly_rate", "tonne_supplement_rate", "voyage_supplement_rate"].filter((key) => Object.prototype.hasOwnProperty.call(account, key)).map((key) => [key, clean(account[key]) || null])),
    ...Object.fromEntries(["tonne_supplement_enabled", "voyage_supplement_enabled"].filter((key) => Object.prototype.hasOwnProperty.call(account, key)).map((key) => [key, Boolean(account[key])])),
  };
  return base;
}

export function buildBillingIssuePayload(invoice) {
  const version = Number(invoice?.version);
  return Number.isInteger(version) && version >= 1 ? { expected_version: version } : {};
}

const get = (path, filters = {}, options = {}, fallback) => request(`${path}${buildBillingQuery(path.startsWith("/items") ? { ...filters, billingSide: "", billing_side: "" } : filters)}`, options, fallback);
const post = (path, body = {}, options = {}, fallback) => request(path, { ...options, body, method: "POST" }, fallback);
const put = (path, body, options = {}, fallback) => request(path, { ...options, body, method: "PUT" }, fallback);
const patch = (path, body, options = {}, fallback) => request(path, { ...options, body, method: "PATCH" }, fallback);
const remove = (path, options = {}, fallback) => request(path, { ...options, method: "DELETE" }, fallback);

async function mutateBillingAccount(operation) {
  const result = await operation();
  invalidatePageDataCacheEntries(["/api/data-manager/state"]);
  return result;
}

export const fetchBillableItems = (filters, options) => get("/items", filters, options, "Impossible de charger les prestations à facturer.");
export const fetchBillableItemSummary = (filters, options) => get("/items/summary", filters, options, "Impossible de charger les indicateurs à facturer.");
export const previewBillingDrafts = (payload, options) => post("/drafts/preview", payload, options, "La vérification des brouillons a échoué.");
export const createBillingDrafts = (payload, options) => post("/drafts", payload, options, "La création des brouillons a échoué.");

// Invoice reads defer the slow Consolidation issue check; fetchInvoiceIssueChecks completes it.
export const fetchInvoices = (filters, options) => get("/invoices", { ...filters, issueChecks: "deferred" }, options, "Impossible de charger les relevés de transport.");
export const fetchInvoiceSummary = (filters, options) => get("/invoices/summary", filters, options, "Impossible de charger les indicateurs de facturation.");
export const fetchInvoice = (invoiceId, options) => get(`/invoices/${id(invoiceId)}`, { issueChecks: "deferred" }, options, "Impossible de charger le relevé de transport.");
export const fetchInvoiceIssueChecks = (invoiceIds, options) => post("/invoices/issue-checks", { invoice_ids: invoiceIds }, options, "La vérification avant l’envoi a échoué.");
export const updateDraftInvoice = (invoiceId, payload, options) => patch(`/invoices/${id(invoiceId)}`, payload, options, "Le brouillon n’a pas pu être enregistré.");
export const removeDraftInvoiceItem = (invoiceId, billableItemId, expectedVersion, options) => remove(`/invoices/${id(invoiceId)}/items/${id(billableItemId)}?expected_version=${encodeURIComponent(expectedVersion)}`, options, "La ligne n’a pas pu être retirée.");
export const createDraftManualLine = (invoiceId, payload, options) => post(`/invoices/${id(invoiceId)}/manual-lines`, payload, options, "La ligne libre n’a pas pu être ajoutée.");
export const removeDraftManualLine = (invoiceId, lineId, expectedVersion, options) => remove(`/invoices/${id(invoiceId)}/lines/${id(lineId)}?expected_version=${encodeURIComponent(expectedVersion)}`, options, "La ligne libre n’a pas pu être retirée.");
export const issueInvoice = (invoiceId, payload = {}, options) => post(`/invoices/${id(invoiceId)}/issue`, payload, options, "L’envoi a échoué.");
export const sendInvoice = (invoiceId, payload = {}, options) => post(`/invoices/${id(invoiceId)}/send`, payload, options, "L’envoi a échoué.");
export const voidInvoice = (invoiceId, payload, options) => post(`/invoices/${id(invoiceId)}/void`, payload, options, "L’annulation a échoué.");
export const deleteDraftInvoice = (invoiceId, options) => remove(`/invoices/${id(invoiceId)}`, options, "Le brouillon n’a pas pu être supprimé.");

export const fetchSourceClients = (options) => get("/source-clients", {}, options, "Impossible de charger les clients du Data Manager.");
export const fetchBillingAccounts = ({ includeArchived = false, ...options } = {}) => request(`/accounts${includeArchived ? "?include_archived=true" : ""}`, options, "Impossible de charger les entreprises de facturation.");
export const updateBillingAccountAssociations = (payload, options) => mutateBillingAccount(
  () => patch("/account-associations", payload, options, "Les associations n’ont pas été enregistrées."),
);
export const createBillingAccount = (payload, options) => mutateBillingAccount(
  () => post("/accounts", payload, options, "L’entreprise de facturation n’a pas été créée."),
);
export const updateBillingAccount = (accountId, payload, options) => mutateBillingAccount(
  () => put(`/accounts/${id(accountId)}`, payload, options, "L’entreprise de facturation n’a pas été enregistrée."),
);
export const archiveBillingAccount = (accountId, payload = {}, options) => mutateBillingAccount(
  () => post(`/accounts/${id(accountId)}/archive`, payload, options, "L’entreprise de facturation n’a pas pu être archivée."),
);

export const fetchIssuerSettings = (options) => get("/issuer-settings", {}, options, "Impossible de charger les paramètres.");
export const updateIssuerSettings = (payload, options) => put("/issuer-settings", payload, options, "Les paramètres n’ont pas été enregistrés.");
export const deleteIssuerLogo = (options) => remove("/issuer-settings/logo", options, "Le logo n’a pas été supprimé.");
export const uploadIssuerLogo = (file, options) => uploadLogo("/issuer-settings/logo", file, options);
// Logo du relevé de la Coop : la réponse donne { logo_data_url } (null une fois retiré).
export const uploadBillingAccountLogo = (accountId, file, options) => uploadLogo(`/accounts/${id(accountId)}/logo`, file, options);
export const deleteBillingAccountLogo = (accountId, options) => remove(`/accounts/${id(accountId)}/logo`, options, "Le logo n’a pas été retiré.");

/** Fichier choisi ou image collée, envoyé tel quel : le serveur le vérifie et le réduit. */
async function uploadLogo(path, file, { companyId = "", signal } = {}) {
  if (!(file instanceof Blob)) throw new BillingApiError("Sélectionnez un fichier de logo valide.");
  const body = new FormData();
  body.append("file", file, clean(file.name) || "logo");
  const response = await billingFetch(`${ROOT}${path}`, {
    body,
    cache: "no-store",
    headers: headers(companyId),
    method: "PUT",
    signal,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new BillingApiError(billingErrorMessage(payload, "Le logo n’a pas été téléversé."), response.status, payload);
  return payload ?? {};
}

export async function downloadInvoicePdf(invoiceId, { companyId = "", documentId = "", filename = "releve.pdf", preview = false } = {}) {
  const documentQuery = clean(documentId) ? `?document_id=${id(documentId)}` : "";
  const response = await billingFetch(`${ROOT}/invoices/${id(invoiceId)}/pdf${documentQuery}`, { headers: headers(companyId) });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new BillingApiError(billingErrorMessage(payload, "Le PDF n’a pas pu être téléchargé."), response.status, payload);
  }
  const url = URL.createObjectURL(await response.blob());
  if (preview) {
    openPdfInNewTab(url);
    revokeObjectUrlLater(url, 60_000);
    return;
  }
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  revokeObjectUrlLater(url, 1000);
}

function fileNameFromDisposition(header, fallback) {
  const match = String(header || "").match(/filename\*?=(?:UTF-8'')?"?([^\";]+)"?/i);
  return clean(match?.[1]) || fallback;
}

function revokeObjectUrlLater(url, delayMs) {
  const timer = setTimeout(() => URL.revokeObjectURL(url), delayMs);
  timer?.unref?.();
}

function openPdfInNewTab(url) {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body?.appendChild?.(link);
  link.click();
  link.remove?.();
}

async function downloadBillingBinary(path, {
  body,
  companyId = "",
  fallbackFileName,
  preview = false,
  signal,
} = {}) {
  const response = await billingFetch(`${ROOT}${path}`, {
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: "no-store",
    headers: headers(companyId, body !== undefined),
    method: body === undefined ? "GET" : "POST",
    signal,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new BillingApiError(billingErrorMessage(payload, "Le fichier n’a pas pu être téléchargé."), response.status, payload);
  }
  const fileName = fileNameFromDisposition(response.headers.get("content-disposition"), fallbackFileName);
  const url = URL.createObjectURL(await response.blob());
  if (preview) {
    openPdfInNewTab(url);
    revokeObjectUrlLater(url, 60_000);
    return fileName;
  }
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  revokeObjectUrlLater(url, 1000);
  return fileName;
}

// Excel du relevé, à recopier dans le logiciel de facturation : le nom vient du serveur (« releve-RT-2026-00004.xlsx »).
export function downloadInvoiceXlsx(invoiceId, options = {}) {
  return downloadBillingBinary(`/invoices/${id(invoiceId)}/export.xlsx`, { ...options, fallbackFileName: "releve.xlsx" });
}
